import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import UserLabel from '../userLabel';
import AsyncListTooltip from '../asyncListTooltip';
import UserCountBadge from '../userCountBadge';
import ShareTypeBox from '../shareTypeBox';

const useStyles = makeStyles(() => ({
  userTypeLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  box: {
    minWidth: 'fit-content',
  },
  badge: {
    maxWidth: 'fit-content',
  },
}));

const UserCount = ({
  className = null,
  customUserCountBadgeClassName = null,
  tooltipTitle = '',
  actionButtonLabel = '',
  addButtonLabel = '',
  userCount = 0,
  shouldDisablePortal = true,
  isReadOnly = false,
  hasActionButton = false,
  hasAddButton = false,
  isTooltipDisabled = false,
  onTooltipAction = () => {},
  onTooltipAdd = () => {},
  goToUserProfile = () => {},
  canAccessUserProfileCheck = () => true,
  getUsers,
  hasUserCount = true,
  handleTypeLabel = () => {},
  shouldHandleTypeLabel = false,
  tooltipLabel = '',
  tooltipButtonProps = {},
  addTooltipButtonProps = {},
  icon = null,
}) => {
  const classes = useStyles();
  const [isOpenedView, setIsOpenedView] = useState(true);

  const getLabelData =
    userCount !== 0 || !hasUserCount
      ? getUsers
      : () => Promise.resolve({ data: null });

  const getVirtualUserSize = useCallback(
    (index, items) => {
      if (shouldHandleTypeLabel) {
        const hasTypeLabel = !!handleTypeLabel(items[index]);

        if (index === items.length - 1) {
          return hasTypeLabel ? 26 : 24;
        }

        return hasTypeLabel ? 34 : 32;
      }

      return index === items.length - 1 ? 24 : 32;
    },
    [shouldHandleTypeLabel, handleTypeLabel]
  );

  const renderUser = (user, isClickable) => {
    const seeTypeLabel = shouldHandleTypeLabel ? handleTypeLabel(user) : false;

    return (
      <div className={classes.userTypeLabel}>
        <UserLabel user={user} isDisabled={isReadOnly || !isClickable} />
        {shouldHandleTypeLabel && seeTypeLabel && (
          <ShareTypeBox className={classes.box} label={seeTypeLabel} />
        )}
      </div>
    );
  };
  const onToggleView = value => {
    setIsOpenedView(value);
  };

  return (
    <div className={classNames({ [classes.badge]: !hasUserCount }, className)}>
      <AsyncListTooltip
        title={tooltipTitle}
        tooltipButtonLabel={actionButtonLabel}
        hasTooltipButton={hasActionButton}
        getLabelData={getLabelData}
        renderLabelItem={renderUser}
        shouldDisablePortal={shouldDisablePortal}
        isTooltipDisabled={isTooltipDisabled}
        isItemClickable={canAccessUserProfileCheck}
        getItemSize={getVirtualUserSize}
        onItemClickHandler={goToUserProfile}
        onTooltipButtonClickHandler={onTooltipAction}
        onAddTooltipButtonClickHandler={onTooltipAdd}
        onToggleView={onToggleView}
        tooltipButtonProps={tooltipButtonProps}
        addTooltipButtonProps={addTooltipButtonProps}
        hasAddButton={hasAddButton}
        addButtonLabel={addButtonLabel}
      >
        <UserCountBadge
          className={customUserCountBadgeClassName}
          hasTooltip={isOpenedView}
          hasUserCount={hasUserCount}
          tooltipLabel={tooltipLabel}
          userCount={userCount || 0}
          icon={icon}
        />
      </AsyncListTooltip>
    </div>
  );
};

UserCount.propTypes = {
  className: PropTypes.string,
  customUserCountBadgeClassName: PropTypes.string,
  actionButtonLabel: PropTypes.string,
  addButtonLabel: PropTypes.string,
  userCount: PropTypes.number,
  isReadOnly: PropTypes.bool,
  canAccessUserProfileCheck: PropTypes.func,
  tooltipLabel: PropTypes.string,
  tooltipTitle: PropTypes.string,
  isTooltipDisabled: PropTypes.bool,
  shouldDisablePortal: PropTypes.bool,
  getUsers: PropTypes.func.isRequired,
  goToUserProfile: PropTypes.func,
  hasActionButton: PropTypes.bool,
  hasUserCount: PropTypes.bool,
  onTooltipAction: PropTypes.func,
  onTooltipAdd: PropTypes.func,
  handleTypeLabel: PropTypes.func,
  shouldHandleTypeLabel: PropTypes.bool,
  hasAddButton: PropTypes.bool,
  icon: PropTypes.object,
  tooltipButtonProps: PropTypes.object,
  addTooltipButtonProps: PropTypes.object,
};

export default UserCount;
